/**
 * §7.33 Travel-restriction effectiveness simulator — Phase 11+ feature wave.
 *
 * Travel restrictions are politically expensive and operationally disruptive.
 * The honest evidence base says they BUY TIME (delay peak by days-to-weeks)
 * but rarely PREVENT spread. Ministers routinely over-believe them in the
 * early-pandemic window — this surface exists to surface the delay-in-days
 * vs cost trade-off explicitly, so the briefing pack can carry the honest
 * "what does this actually achieve" rather than the political wish.
 *
 * ## Model
 *
 *   1. Importation rate (cases/day) from origin → destination at the no-
 *      restriction tier is parameterised by origin prevalence × passenger
 *      flow × pre-symptomatic infection fraction. We do NOT predict the
 *      absolute number (that would require live aviation flow data not
 *      yet wired); operators set a baseline importations/day and the
 *      surface scales the tiers from there.
 *   2. Each restriction tier carries a literature-anchored "importation
 *      reduction factor" k_T ∈ [0,1]. Cases imported under tier T per day
 *      = baseline_imports × (1 − k_T).
 *   3. The destination's epidemic-onset day shifts by the delay caused by
 *      the lower importation rate. With local R_eff > 1, the time for
 *      imported seeds to grow to a given threshold (we use I=1000 as the
 *      "epidemic-onset" marker) shifts by Δt ≈ ln(rate_baseline /
 *      rate_restricted) / r_growth where r_growth is the daily exponential
 *      growth rate from the catalog R0 + generation interval.
 *   4. We run simulateSeird at the destination twice: once starting at
 *      day 0 (no restriction) and once starting at day Δt (restriction
 *      delays the take-off). Difference at day 90 = % cases prevented.
 *
 * ## Honesty rail
 *
 * The headline finding for every realistic pathogen + tier combination is
 * that closure buys days-to-weeks of delay, NOT prevention. The honesty
 * banner makes this explicit and cites Chinazzi 2020 + Mateus 2014 — the
 * two papers ministers should be made aware of before any closure
 * decision. The "% cases prevented at 90 days" KPI deliberately collapses
 * to near zero for closure-tier scenarios with R_eff > 1.5 — that is the
 * point.
 *
 * ## Literature anchors
 *
 *   - Chinazzi M, Davis JT, Ajelli M, et al. "The effect of travel
 *     restrictions on the spread of the 2019 novel coronavirus (COVID-19)
 *     outbreak." Science 2020. PMID 32144116. Wuhan travel ban delayed
 *     COVID-19 spread in mainland China by 3-5 days; international 2-3
 *     weeks; ~80% reduction in international case importation BUT only
 *     when combined with >50% reduction in local transmission — restriction
 *     alone bought time, did not prevent.
 *
 *   - Mateus AL, Otete HE, Beck CR, Dolan GP, Nguyen-Van-Tam JS.
 *     "Effectiveness of travel restrictions in the rapid containment of
 *     human influenza: a systematic review." Bull World Health Organ 2014.
 *     PMID 25431558. Systematic review of 23 studies: international travel
 *     restrictions delayed pandemic flu introduction by ~3 weeks at best;
 *     internal restrictions delayed peak by 2 months in isolated cases.
 *     Never prevented community spread once R_eff > 1.
 *
 *   - Ryu S, Gao H, Wong JY, et al. "Nonpharmaceutical Measures for
 *     Pandemic Influenza in Nonhealthcare Settings — International Travel-
 *     Related Measures." Emerg Infect Dis 2020. Entry screening detects
 *     <30% of infected travellers because of pre-symptomatic spread; exit
 *     screening similarly weak. Anchors the "screening" tier multiplier.
 *
 *   - Mouchtouri VA, et al. "Exit + entry screening practices for
 *     infectious diseases among travellers at points of entry: looking
 *     for evidence on public health impact." Int J Environ Res Public
 *     Health 2019. PMID 31752440. Detection sensitivity ~10-30% for
 *     incubating travellers.
 *
 * ## Cost anchors (GDP impact per quarter at the restriction tier)
 *
 *   - IMF World Economic Outlook update, April 2021 + Oct 2021. Pandemic-
 *     era travel + mobility restrictions estimated at 0.1-0.5% of GDP per
 *     quarter for the typical advanced economy during 2020-2021. Heavy
 *     reliance on tourism (e.g. Maldives, Greece, Thailand) pushes this
 *     up by 2-5× per quarter.
 *   - ICAO Effects of Novel Coronavirus on Civil Aviation: Economic Impact
 *     Analysis (2020-2022 series). International passenger volumes fell
 *     ~60% in 2020 vs 2019; revenue loss ~USD 370bn. Used here to anchor
 *     the closure tier cost as the upper bound.
 *   - OECD Tourism Trends and Policies 2022 — for the tourism-dependent
 *     economy multiplier (×2.5 default for SIDS / tourism > 20% GDP).
 *
 * The cost numbers are deliberately presented as ranges, not point
 * estimates. The point of the cost panel is to make ministers see that
 * closure scales from "noticeable" to "catastrophic" depending on the
 * destination's tourism dependency — not to be the canonical GDP forecast.
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { findRegion, listRegions } from '@biomax/plugin-region-database';
import { simulateSeird } from '@biomax/plugin-epidemiology';
import { Sparkline } from '../sparkline';
import { PinToBriefButton } from '../pack/PinToBriefButton';
import { SurfaceScaffold } from '../SurfaceScaffold';
import { HonestyRail } from '../HonestyRail';

/** Restriction tier. Order matters — sequenced strictest-last so the UI
 *  picker reads as an escalation ladder. */
type RestrictionTierId = 'none' | 'screening' | 'q7d' | 'q14d' | 'closure';

interface RestrictionTier {
  id: RestrictionTierId;
  label: string;
  /** Fraction [0,1] of imported infections prevented by this tier. */
  importationReduction: number;
  /** GDP impact per quarter at this tier (% of national GDP), low end. */
  gdpCostPctPerQuarterLow: number;
  /** GDP impact per quarter at this tier, high end. */
  gdpCostPctPerQuarterHigh: number;
  /** Primary literature anchor for the reduction factor. */
  evidenceAnchor: string;
  /** Short operator-facing rationale. */
  rationale: string;
}

const RESTRICTION_TIERS: readonly RestrictionTier[] = [
  {
    id: 'none',
    label: 'None (open borders)',
    importationReduction: 0,
    gdpCostPctPerQuarterLow: 0,
    gdpCostPctPerQuarterHigh: 0,
    evidenceAnchor: 'baseline',
    rationale: 'No restriction — importation rate set by origin prevalence × passenger flow.',
  },
  {
    id: 'screening',
    label: 'Entry/exit symptom screening',
    importationReduction: 0.20,  // Ryu 2020 + Mouchtouri 2019: ~10-30% sensitivity, midpoint ~20%
    gdpCostPctPerQuarterLow: 0.01,
    gdpCostPctPerQuarterHigh: 0.05,
    evidenceAnchor: 'Ryu 2020 (EID) + Mouchtouri 2019 (PMID 31752440)',
    rationale: 'Symptom + thermal screening detects ~10-30% of incubating travellers; misses pre-symptomatic. Low cost, low effectiveness.',
  },
  {
    id: 'q7d',
    label: '7-day quarantine on arrival',
    importationReduction: 0.55,  // ~55% of imported infections caught before transmissibility window ends (catalog-pathogen avg)
    gdpCostPctPerQuarterLow: 0.05,
    gdpCostPctPerQuarterHigh: 0.20,
    evidenceAnchor: 'Wells 2020 (PNAS) + Chinazzi 2020 (PMID 32144116) post-hoc',
    rationale: '7-day Q catches most incubating cases for short-incubation pathogens (median COVID ~5d); leaks long-tail.',
  },
  {
    id: 'q14d',
    label: '14-day quarantine on arrival',
    importationReduction: 0.85,  // 14d covers >95% of COVID incubation; weighted down for compliance + asymptomatic
    gdpCostPctPerQuarterLow: 0.10,
    gdpCostPctPerQuarterHigh: 0.30,
    evidenceAnchor: 'Lauer 2020 (Annals IM) + Chinazzi 2020 (PMID 32144116)',
    rationale: '14-day Q covers >95% of COVID-class incubation distribution; effectiveness limited by compliance + asymptomatic transmission.',
  },
  {
    id: 'closure',
    label: 'Full border closure',
    importationReduction: 0.95,  // Even with closure, repatriation + freight + medevac leak (Chinazzi: ~80-95% reduction observed)
    gdpCostPctPerQuarterLow: 0.10,
    gdpCostPctPerQuarterHigh: 0.50,
    evidenceAnchor: 'Chinazzi 2020 (PMID 32144116) Wuhan ban + IMF WEO 2021',
    rationale: 'Closure leaks via repatriation + freight + medevac. Wuhan ban achieved ~80-95% reduction for 50 days only; epidemic still propagated.',
  },
];

/** Tourism-dependency multiplier on the GDP cost band. SIDS + tourism-
 *  reliant economies amplify the cost significantly (OECD 2022). */
interface TourismProfile {
  id: 'low' | 'med' | 'high';
  label: string;
  multiplier: number;
  example: string;
}

const TOURISM_PROFILES: readonly TourismProfile[] = [
  { id: 'low',  label: 'Tourism < 5% GDP',        multiplier: 1.0, example: 'most advanced economies (USA, DEU, JPN)' },
  { id: 'med',  label: 'Tourism 5-20% GDP',       multiplier: 1.6, example: 'tourism-active mid-income (THA, ESP, ITA)' },
  { id: 'high', label: 'Tourism > 20% GDP (SIDS)', multiplier: 2.8, example: 'tourism-dependent SIDS (MDV, BHS, BRB)' },
];

function tierById(id: RestrictionTierId): RestrictionTier {
  return RESTRICTION_TIERS.find(t => t.id === id) ?? RESTRICTION_TIERS[0]!;
}

export function TravelRestrictionSim() {
  const pathogens = listPathogens();
  const regions = listRegions();

  const [pathogenId, setPathogenId] = useState<string>('sars-cov-2-wildtype');
  const [originRegionId, setOriginRegionId] = useState<string>('chn');
  const [destinationRegionId, setDestinationRegionId] = useState<string>('gbr');
  const [tierId, setTierId] = useState<RestrictionTierId>('q14d');
  /** Operator-tunable baseline importations/day at the "none" tier.
   *  Default ~5/day matches early-2020 China→UK estimates (Pullano 2020). */
  const [baselineImportsPerDay, setBaselineImportsPerDay] = useState<number>(5);
  const [tourismProfileId, setTourismProfileId] = useState<TourismProfile['id']>('med');
  const [horizonDays, setHorizonDays] = useState<number>(120);

  const view = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    const destination = findRegion(destinationRegionId);
    if (!pathogen || !destination) return null;

    const tier = tierById(tierId);

    // Reproductive number + generation interval → growth rate r = (R0 − 1) / Tg.
    // Use latent + half-infectious as a serial-interval proxy; coarse but
    // catalog-aligned. Floor at small positive number to avoid div-by-zero
    // for borderline-controlled pathogens.
    const R0 = pathogen.R0.value;
    const Tg = pathogen.latentPeriodDays.value + pathogen.infectiousPeriodDays.value / 2;
    const dailyGrowthRate = Math.max(0.001, Math.log(Math.max(1.001, R0)) / Math.max(1, Tg));

    const importsWithTier = baselineImportsPerDay * (1 - tier.importationReduction);
    // Days to first imported infection: 1 / λ for a Poisson-like process at
    // either tier. Closure-tier with importsWithTier near zero floors at the
    // horizon to flag "no imports expected in window".
    const daysToFirstImportNone     = baselineImportsPerDay > 0
      ? Math.ceil(1 / baselineImportsPerDay) : horizonDays;
    const daysToFirstImportWithTier = importsWithTier > 0.001
      ? Math.ceil(1 / importsWithTier) : horizonDays;

    // Days bought: ln(rate_baseline / rate_restricted) / r_growth.
    // Closes to ~0 when tier reduction is 0; goes to infinity (clamped to
    // horizon) when reduction approaches 1.
    let daysBought: number;
    if (tier.importationReduction <= 0) {
      daysBought = 0;
    } else if (tier.importationReduction >= 0.999) {
      daysBought = horizonDays;
    } else {
      const ratio = baselineImportsPerDay / Math.max(0.0001, importsWithTier);
      daysBought = Math.min(horizonDays, Math.log(ratio) / dailyGrowthRate);
    }
    daysBought = Math.max(0, daysBought);

    // Destination SEIRD: same parameters, two starts.
    // Use I0 = 1 (single imported case at day 0 for baseline, at day Δt for
    // restricted scenario). Population denominator = destination total.
    const N = destination.population.total;
    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    const beta = R0 * gamma;
    const ifrBand = pathogen.ifrByAgeBand[Math.floor(pathogen.ifrByAgeBand.length / 2)];
    const ifr = ifrBand?.ifr ?? 0.005;

    const baseline = simulateSeird({
      N, I0: 1, beta, gamma, sigma, ifr,
      durationDays: horizonDays, dtDays: 1,
    });

    // Restricted: same SEIRD curve, but onset shifted by daysBought. We
    // model this by running the same simulation for (horizonDays - daysBought)
    // days and prepending zeros — clean, deterministic, no second-engine run.
    const restrictedHorizon = Math.max(1, Math.floor(horizonDays - daysBought));
    const restrictedRun = simulateSeird({
      N, I0: 1, beta, gamma, sigma, ifr,
      durationDays: restrictedHorizon, dtDays: 1,
    });

    function shiftedSeries(series: number[]): number[] {
      const pad = new Array(Math.floor(daysBought)).fill(0);
      const tail = series.slice(0, horizonDays - pad.length);
      const out = pad.concat(tail);
      // Pad to exactly horizonDays + 1 (SEIRD includes t0).
      while (out.length < baseline.compartments.I!.length) out.push(out[out.length - 1] ?? 0);
      return out.slice(0, baseline.compartments.I!.length);
    }

    const restrictedI = shiftedSeries(restrictedRun.compartments.I!);
    const restrictedD = shiftedSeries(restrictedRun.compartments.D!);

    const baselineI = baseline.compartments.I!;
    const baselineD = baseline.compartments.D!;

    // Epidemic-onset days: first day I ≥ 1000 (operator-meaningful threshold).
    function dayToThreshold(series: number[], threshold: number): number | null {
      for (let t = 0; t < series.length; t++) {
        if (series[t]! >= threshold) return t;
      }
      return null;
    }
    const baselineOnsetDay   = dayToThreshold(baselineI, 1000);
    const restrictedOnsetDay = dayToThreshold(restrictedI, 1000);

    // % cases prevented at end of horizon. Cumulative infected ≈ N − S.
    const baselineCumInfectedEnd =
      N - baseline.compartments.S!.at(-1)!;
    const restrictedCumInfectedEnd =
      N - (restrictedRun.compartments.S!.at(restrictedHorizon)
            ?? restrictedRun.compartments.S!.at(-1)!);
    const pctPreventedAtHorizon = baselineCumInfectedEnd > 0
      ? ((baselineCumInfectedEnd - restrictedCumInfectedEnd) / baselineCumInfectedEnd) * 100
      : 0;

    // Imported cases averted over horizon (linear at rate × days).
    const importedAverted = Math.round(
      (baselineImportsPerDay - importsWithTier) * horizonDays
    );

    // GDP cost band, multiplied by tourism profile.
    const tourism = TOURISM_PROFILES.find(t => t.id === tourismProfileId)!;
    const gdpLow  = tier.gdpCostPctPerQuarterLow  * tourism.multiplier;
    const gdpHigh = tier.gdpCostPctPerQuarterHigh * tourism.multiplier;

    return {
      pathogen, destination, tier, tourism,
      dailyGrowthRate,
      importsWithTier,
      daysToFirstImportNone, daysToFirstImportWithTier,
      daysBought,
      baselineOnsetDay, restrictedOnsetDay,
      pctPreventedAtHorizon,
      importedAverted,
      gdpLow, gdpHigh,
      baselineI, restrictedI,
      baselineD, restrictedD,
      receipt: baseline.receipt,
      baselineCumInfectedEnd: Math.round(baselineCumInfectedEnd),
      restrictedCumInfectedEnd: Math.round(restrictedCumInfectedEnd),
    };
  }, [pathogenId, destinationRegionId, tierId, baselineImportsPerDay, tourismProfileId, horizonDays]);

  if (!view) return <div>Loading…</div>;
  const origin = findRegion(originRegionId);

  return (
    <SurfaceScaffold
      section="§7.33"
      title="Travel-restriction effectiveness simulator"
      subtitle={
        <>
          Origin {origin?.names.english ?? originRegionId} → destination {view.destination.names.english} ·
          {' '}tier <strong>{view.tier.label}</strong> · {view.pathogen.names.common} ·
          {' '}<span className="receipt"><span className="receipt__dot" />engine {view.receipt.engineVersion} · {view.receipt.elapsedMs.toFixed(0)} ms · {view.receipt.steps} steps</span>
        </>
      }
      honestyRail={
        <HonestyRail variant="limitation" label="DELAY ≠ PREVENT">
          Honest evidence base: travel restrictions buy <strong>days to weeks</strong> of delay
          for the destination, NOT prevention.
          {' '}<em>Chinazzi 2020</em> (PMID 32144116) showed the Wuhan ban delayed COVID-19
          mainland-China spread by 3-5 days and international spread by 2-3 weeks —
          but only when paired with &gt;50% local-transmission reduction.
          {' '}<em>Mateus 2014</em> (PMID 25431558) systematic review: international
          travel restrictions for pandemic flu delayed introduction by ~3 weeks at best;
          never prevented spread once local R_eff &gt; 1. Political pressure for
          closure routinely exceeds what the science can deliver — surface this
          when briefing.
        </HonestyRail>
      }
      kpis={
        <>
          <div className="kpis">
            <div className="kpi">
              <div className="kpi__label">Days bought</div>
              <div className="kpi__value kpi__value--signal">
                {view.daysBought.toFixed(0)}d
              </div>
              <div className="kpi__sub">
                delay to epidemic onset · {view.tier.importationReduction === 0 ? '—' : `tier ${view.tier.label}`}
              </div>
            </div>
            <div className="kpi">
              <div className="kpi__label">Day to first imported case</div>
              <div className="kpi__value">
                d{view.daysToFirstImportWithTier}
              </div>
              <div className="kpi__sub">
                vs d{view.daysToFirstImportNone} at open borders
              </div>
            </div>
            <div className="kpi">
              <div className="kpi__label">Imported cases averted</div>
              <div className="kpi__value">{view.importedAverted.toLocaleString()}</div>
              <div className="kpi__sub">over {horizonDays}d horizon</div>
            </div>
            <div className="kpi">
              <div className="kpi__label">% cases prevented at d{horizonDays}</div>
              <div className="kpi__value kpi__value--danger">
                {view.pctPreventedAtHorizon.toFixed(1)}%
              </div>
              <div className="kpi__sub">
                cumulative infections in destination
              </div>
            </div>
            <div className="kpi">
              <div className="kpi__label">GDP cost · per quarter</div>
              <div className="kpi__value">
                {view.gdpLow.toFixed(2)}–{view.gdpHigh.toFixed(2)}%
              </div>
              <div className="kpi__sub">
                tourism × {view.tourism.multiplier.toFixed(1)} (IMF WEO 2021 + ICAO)
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '8px 0' }}>
            <PinToBriefButton item={{
              id: `travel-restrictions-${pathogenId}-${originRegionId}-${destinationRegionId}-${tierId}-d${horizonDays}-imp${baselineImportsPerDay}`,
              surface: '§7.33',
              label: `${view.pathogen.names.common} ${origin?.names.english ?? originRegionId}→${view.destination.names.english} · ${view.tier.label} · +${view.daysBought.toFixed(0)}d bought · ${view.pctPreventedAtHorizon.toFixed(1)}% prevented at d${horizonDays}`,
              value: Math.round(view.daysBought),
              context: {
                pathogenId,
                origin: originRegionId,
                destination: destinationRegionId,
                tier: view.tier.id,
                tierLabel: view.tier.label,
                tierEvidence: view.tier.evidenceAnchor,
                baselineImportsPerDay,
                tourismProfile: view.tourism.id,
                horizonDays,
                daysBought: Math.round(view.daysBought),
                importedAverted: view.importedAverted,
                pctPreventedAtHorizon: Number(view.pctPreventedAtHorizon.toFixed(1)),
                gdpLowPct: Number(view.gdpLow.toFixed(2)),
                gdpHighPct: Number(view.gdpHigh.toFixed(2)),
                honestyRail: 'Travel restrictions delay, rarely prevent (Chinazzi 2020 PMID 32144116; Mateus 2014 PMID 25431558).',
              },
            }} />
          </div>
        </>
      }
      inputs={
        <div className="panel">
          <div className="panel__head"><span className="panel__title">Inputs</span></div>
          <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Origin region (signal source)</span>
            <select value={originRegionId} onChange={e => setOriginRegionId(e.target.value)}>
              {regions.slice().sort((a, b) => a.names.english.localeCompare(b.names.english))
                .map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Destination region</span>
            <select value={destinationRegionId} onChange={e => setDestinationRegionId(e.target.value)}>
              {regions.slice().sort((a, b) => a.names.english.localeCompare(b.names.english))
                .map(r => <option key={r.id} value={r.id}>{r.names.english}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Restriction tier</span>
            <select value={tierId} onChange={e => setTierId(e.target.value as RestrictionTierId)}>
              {RESTRICTION_TIERS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Baseline imports/day · open borders</span>
            <input
              type="number" min="0.1" max="500" step="0.1"
              value={baselineImportsPerDay}
              onChange={e => setBaselineImportsPerDay(parseFloat(e.target.value) || 0.1)}
            />
            <span style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', marginTop: 4 }}>
              Operator-tunable. Early-2020 China→UK estimate ~5/day (Pullano 2020); high-flow
              corridors (USA↔MEX) reach 50+/day. Until live aviation flow ingest lands, this
              is the dial that scales the absolute importation numbers.
            </span>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Tourism dependency of destination</span>
            <select
              value={tourismProfileId}
              onChange={e => setTourismProfileId(e.target.value as TourismProfile['id'])}
            >
              {TOURISM_PROFILES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <span style={{ fontSize: 10.5, color: 'var(--bm-text-subtle)', marginTop: 4 }}>
              Scales the GDP cost band. Example: {view.tourism.example}.
            </span>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Horizon · days</span>
            <input
              type="number" min="30" max="365" step="10"
              value={horizonDays}
              onChange={e => setHorizonDays(parseInt(e.target.value, 10) || 90)}
            />
          </label>
          </div>
        </div>
      }
    >
      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Restriction tier ladder · evidence + cost</span>
          <span className="panel__meta">click row to switch tier</span>
        </div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Tier</th>
              <th className="num">Importation reduction</th>
              <th className="num">GDP cost / quarter</th>
              <th>Evidence anchor</th>
              <th>Rationale</th>
            </tr>
          </thead>
          <tbody>
            {RESTRICTION_TIERS.map(t => {
              const active = t.id === tierId;
              return (
                <tr
                  key={t.id}
                  onClick={() => setTierId(t.id)}
                  style={{
                    cursor: 'pointer',
                    background: active ? 'rgba(110,163,255,0.08)' : 'transparent',
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  <td>{t.label}</td>
                  <td className="num">{(t.importationReduction * 100).toFixed(0)}%</td>
                  <td className="num">
                    {(t.gdpCostPctPerQuarterLow * view.tourism.multiplier).toFixed(2)}
                    –{(t.gdpCostPctPerQuarterHigh * view.tourism.multiplier).toFixed(2)}%
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>{t.evidenceAnchor}</td>
                  <td style={{ fontSize: 11.5, color: 'var(--bm-text-muted)' }}>{t.rationale}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Destination infectious trajectory · baseline vs restricted</span>
          <span className="panel__meta">
            onset (I ≥ 1000): baseline d{view.baselineOnsetDay ?? '—'} · restricted d{view.restrictedOnsetDay ?? '—'}
          </span>
        </div>
        <Sparkline values={view.baselineI} label="No restriction (I_baseline)" stroke="var(--bm-danger)" />
        <Sparkline values={view.restrictedI} label={`${view.tier.label} (I_restricted)`} stroke="var(--bm-accent)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Destination cumulative deaths · baseline vs restricted</span>
          <span className="panel__meta">D compartment</span>
        </div>
        <Sparkline values={view.baselineD} label="No restriction (D_baseline)" stroke="var(--bm-danger)" />
        <Sparkline values={view.restrictedD} label={`${view.tier.label} (D_restricted)`} stroke="var(--bm-accent)" />
      </div>

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Method + limitations</span>
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--bm-text)' }}>
          Importation rate at tier T = baseline × (1 − k_T) where k_T is the
          literature-anchored reduction factor (table above). Destination
          epidemic-onset delay Δt ≈ ln(rate_baseline / rate_restricted) / r,
          with r = ln(R0) / generation-interval from the verified pathogen
          catalog (R0 = {view.pathogen.R0.value.toFixed(2)}, Tg ≈ {(view.pathogen.latentPeriodDays.value + view.pathogen.infectiousPeriodDays.value / 2).toFixed(1)}d ⇒ r = {view.dailyGrowthRate.toFixed(3)}/d).
          Same SEIRD trajectory runs for both arms — restricted arm is just
          shifted by Δt. <strong>Pre-symptomatic transmission is the main
          reason no tier reaches 100% reduction</strong> (Ryu 2020). Closure
          tier capped at 95% because repatriation, freight, medevac, and
          land-border evasion routinely leak (Chinazzi 2020).
          <br /><br />
          <strong>Tracked v0.3 limitations.</strong>
          {' '}(a) Baseline imports/day is operator-tunable, NOT pulled from a
          live aviation feed — wire to DTU OpenSky / IATA AirportIS when
          partnership lands. (b) Reduction factors are constant across
          pathogens; ideally they scale with the pathogen's pre-symptomatic
          fraction + incubation distribution. (c) GDP cost band ignores
          second-order effects (tourism rebound, supply-chain disruption,
          consumer-confidence effects) — IMF WEO 2021 anchors first-order
          travel + mobility losses only. (d) No origin-region containment
          coupling — when origin succeeds in driving R_eff &lt; 1, baseline
          imports/day decays organically (not modelled here; use §7.16
          Border Control for that).
        </div>
      </div>
    </SurfaceScaffold>
  );
}
